import type {
  CompetitionRegistrationEntry,
  PublicCompetition,
  PublicCompetitionDivision,
} from '@tark/shared/lib';

const actionErrorMessages: Record<string, string> = {
  COMPETITION_NOT_FOUND: 'This competition link is no longer available.',
  DIVISION_NOT_FOUND: 'That division is no longer available.',
  REGISTRATION_NOT_OPEN: 'Registration is not open right now.',
  REGISTRATION_CLOSED: 'Registration has closed for this division.',
  DIVISION_NOT_AVAILABLE: 'This division is not available for registration yet.',
  ENTRY_ALREADY_EXISTS: 'A registration already exists for this division.',
  ENTRY_TYPE_INVALID: 'This registration option no longer matches the division.',
  PAIR_INVITE_NOT_ALLOWED: 'Partner invitations are not available for this division.',
  PAIR_INVITE_NOT_FOUND: 'This partner invitation is invalid or has expired.',
  PAIR_INVITE_ALREADY_USED: 'This partner invitation has already been used.',
  PAIR_INVITE_SELF_ACCEPT_NOT_ALLOWED: 'The player who created this link cannot accept it.',
  COMPETITION_CLAIM_NOT_FOUND: 'This registration link is invalid or has expired.',
  COMPETITION_ACCESS_CONFLICT: 'This competition is already linked to another player record.',
  ACCOUNT_SETUP_REQUIRED: 'Finish setting up your TARK account before registering.',
};

export function getCompetitionActionError(error: unknown): string {
  const response = (error as {
    response?: { status?: number; data?: { code?: string; error?: string } };
  })?.response;
  const code = response?.data?.code || response?.data?.error;
  if (code && actionErrorMessages[code]) {
    return actionErrorMessages[code];
  }
  if (response?.status === 401) {
    return 'Sign in to continue.';
  }
  if (response?.status === 404) {
    return 'This competition item is no longer available.';
  }
  if (response?.status === 409) {
    return 'This registration changed. Refresh and try again.';
  }
  return 'TARK could not complete that action. Please try again.';
}

export function formatCompetitionDate(
  value: string | null | undefined,
  timezone?: string,
  includeTime = false
): string | null {
  if (!value) {
    return null;
  }
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...(includeTime ? { hour: 'numeric', minute: '2-digit' } : {}),
      ...(timezone ? { timeZone: timezone } : {}),
    }).format(new Date(value));
  } catch {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...(includeTime ? { hour: 'numeric', minute: '2-digit' } : {}),
    }).format(new Date(value));
  }
}

export function formatCompetitionRange(competition: PublicCompetition): string {
  const start = formatCompetitionDate(competition.competitionStartsAt, competition.timezone);
  const end = formatCompetitionDate(competition.competitionEndsAt, competition.timezone);
  if (start && end && start !== end) {
    return `${start} - ${end}`;
  }
  return start || end || 'Dates to be announced';
}

export function formatDivisionFormat(formatCode: string): string {
  switch (formatCode) {
    case 'SINGLES':
      return 'Singles';
    case 'FIXED_DOUBLES':
      return 'Fixed doubles';
    case 'ROTATING_DOUBLES':
      return 'Rotating doubles';
    default:
      return formatEnum(formatCode);
  }
}

export function formatEnum(value: string): string {
  return value
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function isFixedPairDivision(division: PublicCompetitionDivision): boolean {
  return division.formatCode === 'FIXED_DOUBLES';
}

export function isDivisionOpen(
  competition: PublicCompetition,
  division: PublicCompetitionDivision
): boolean {
  return (
    competition.registrationState === 'OPEN' &&
    division.registrationState === 'OPEN' &&
    division.formatCode !== 'ROTATING_DOUBLES'
  );
}

export function findDivisionEntry(
  entries: CompetitionRegistrationEntry[] | undefined,
  divisionPublicId: string
): CompetitionRegistrationEntry | undefined {
  return entries?.find((entry) => entry.divisionPublicId === divisionPublicId);
}

export function getEntryStatusLabel(entry: CompetitionRegistrationEntry): string {
  if (entry.status === 'RESERVE' && entry.reservePosition) {
    return `Reserve ${entry.reservePosition}`;
  }
  return formatEnum(entry.status);
}

export function getDivisionAvailabilityLabel(
  competition: PublicCompetition,
  division: PublicCompetitionDivision
): string {
  if (division.formatCode === 'ROTATING_DOUBLES') {
    return 'Registration coming later';
  }
  if (competition.registrationState === 'UPCOMING') {
    return 'Registration opens soon';
  }
  if (competition.registrationState !== 'OPEN' || division.registrationState !== 'OPEN') {
    return division.registrationState === 'FULL' ? 'Division full' : 'Registration closed';
  }
  return 'Registration open';
}
